package ml

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * Simple token store - avoids Kotlin interface / SKIE bridging complexity.
 * Swift calls SimpleMLIntegrationKt.setAIAuthToken() to keep the token current.
 */
object AICategoryTokenStore {
    var authToken: String? = null
}

/**
 * Client for calling the server-side AI categorization API.
 * Reads auth token from AICategoryTokenStore at request time.
 */
class AICategoryApiClient(
    private val baseUrl: String = "http://localhost:3000",
    private var httpClient: HttpClient = createDefaultHttpClient()
) {
    /** Close the underlying HttpClient to free connection pools / memory. */
    fun close() {
        httpClient.close()
    }

    /** Re-create the HttpClient after it was closed (e.g., returning from background). */
    fun reopen() {
        httpClient = createDefaultHttpClient()
    }

    companion object {
        fun createDefaultHttpClient() = HttpClient {
            install(ContentNegotiation) {
                json(Json {
                    ignoreUnknownKeys = true
                    encodeDefaults = true
                })
            }
        }
    }

    suspend fun categorizeTransaction(
        merchantName: String,
        amount: Double,
        description: String = ""
    ): CategoryApiResponse? {
        return try {
            val token = AICategoryTokenStore.authToken ?: return null

            val response = httpClient.post("$baseUrl/api/categorize") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody(CategoryApiRequest(merchantName, amount, description))
            }

            if (response.status == HttpStatusCode.OK) {
                response.body<CategoryApiResponse>()
            } else {
                null
            }
        } catch (e: Exception) {
            println("Failed to categorize via API: ${e.message}")
            null
        }
    }

    suspend fun categorizeBatch(
        transactions: List<CategoryApiRequest>
    ): List<CategoryApiResponse> {
        return try {
            val token = AICategoryTokenStore.authToken ?: return emptyList()

            val response = httpClient.post("$baseUrl/api/categorize/batch") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody(BatchCategoryApiRequest(transactions))
            }

            if (response.status == HttpStatusCode.OK) {
                response.body<BatchCategoryApiResponse>().results
            } else {
                emptyList()
            }
        } catch (e: Exception) {
            println("Failed to batch categorize via API: ${e.message}")
            emptyList()
        }
    }
}

@Serializable
data class CategoryApiRequest(
    val merchantName: String,
    val amount: Double,
    val description: String? = null
)

@Serializable
data class CategoryApiResponse(
    val primaryCategory: String,
    val detailedCategory: String,
    val confidence: Double,
    val source: String
)

@Serializable
data class BatchCategoryApiRequest(
    val transactions: List<CategoryApiRequest>
)

@Serializable
data class BatchCategoryApiResponse(
    val results: List<CategoryApiResponse>
)
